import { keyframes } from '@emotion/react'
import { Button, Collapse, Tooltip } from '@mantine/core'
import { getMapOverlaysPadding } from 'App/Map/CurrentValuesMap/_utils/getMapOverlaysPadding'
import { MapContext } from 'App/Map/MapContext/MapContext'
import { getNodeStatusLabel } from 'App/ServiceCenter/Map/_utils/getActiveNodeStatus'
import { OverlayView } from 'App/ServiceCenter/Map/_utils/OverlayView'
import { serviceCenterStore } from 'App/ServiceCenter/store/serviceCenterStore'
import { ExpandableChevronIndicator } from 'components/ExpandableChevronIndicator/ExpandableChevronIndicator'
import { IconChevronCircle } from 'components/icons/IconChevronCircle'
import { translate } from 'i18n/i18n'
import _ from 'lodash'
import React, { useContext, useMemo } from 'react'
import { colors } from 'settings/colors'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { getPropertyBounds } from 'utils/useMapCenteredOnProperties'
import { TNodeStatus, type TPropertyEquipmentSummary } from '../types'
import { EQUIPMENT_COUNT_BADGE_CLASS, EquipmentCountBadge } from './EquipmentCountBadge'
import { EquipmentStatusWithCountBadge } from './EquipmentStatusWithCountBadge'

const PROPERTY_CARD_DIMENSIONS = {
  wideScreen: {
    maxWidth: 350,
    fontSize: '16px',
    gridTemplateColumns: '150px 150px',
  },
  mobileScreen: {
    maxWidth: 290,
    fontSize: '14px',
    gridTemplateColumns: '120px 120px',
  },
}

export const PropertySummary: React.FC<{
  property: TProperty
  summaryData?: TPropertyEquipmentSummary
  loading: boolean
  opened: boolean
  onToggleOpen: () => void
}> = ({ property, summaryData, opened, loading, onToggleOpen }) => {
  const { map } = useContext(MapContext)
  const isWideScreen = useIsWideScreen()

  const { onlineNodesCount, offlineNodesCount, errorNodesCount, plannedNodesCount } =
    summaryData ||
    // If data has been loaded, but there is still no summary for this property, default values to 0
    (loading ? {} : { onlineNodesCount: 0, offlineNodesCount: 0, errorNodesCount: 0, plannedNodesCount: 0 })

  if (!property) return null

  const propertyBounds = useMemo(() => getPropertyBounds(property.propertyId), [property])

  const propertyCenter = useMemo(() => {
    if (propertyBounds && !propertyBounds.isEmpty()) {
      return {
        lat: propertyBounds.getNorthEast().lat(),
        lng: propertyBounds.getCenter().lng(),
      }
    }

    return {
      lat: property.centroid.coordinates.lat,
      lng: property.centroid.coordinates.lng,
    }
  }, [property.centroid, propertyBounds])

  const handleClickViewProperty = () => {
    if (!_.isEmpty(propertyBounds)) {
      map?.fitBounds(propertyBounds, getMapOverlaysPadding())
    }

    const zoomLevel = map?.getZoom()

    serviceCenterStore.actions.setPropertyOverviewZoomLevel(zoomLevel)

    serviceCenterStore.actions.selectProperty(property.propertyId)
  }

  return (
    <OverlayView
      position={propertyCenter}
      layer={opened ? 'floatPane' : 'overlayMouseTarget'}
      prevent="MAP_HITS_AND_GESTURES"
      containerStyle={{
        zIndex: opened ? 100000 : 999,
        transform: 'translate(-50%, -110%)',
      }}
    >
      <div
        key={property.propertyId}
        css={{
          backgroundColor: colors.midnight,
          maxWidth: isWideScreen
            ? PROPERTY_CARD_DIMENSIONS.wideScreen.maxWidth
            : PROPERTY_CARD_DIMENSIONS.mobileScreen.maxWidth,
          border: '2px solid white',
          borderRadius: 20,
          padding: '8px 20px',
        }}
        onClick={onToggleOpen}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            color: 'white',
            cursor: 'pointer',
            minHeight: 25,
          }}
        >
          <ExpandableChevronIndicator expanded={opened} size={16} />

          <div
            style={{
              marginLeft: 10,
              marginRight: 6,
              fontSize: isWideScreen
                ? PROPERTY_CARD_DIMENSIONS.wideScreen.fontSize
                : PROPERTY_CARD_DIMENSIONS.mobileScreen.fontSize,
              fontWeight: 500,
            }}
          >
            {property.propertyName}
          </div>

          {!opened && (
            <div
              css={{
                display: 'flex',
                ...(loading && {
                  [`& .${EQUIPMENT_COUNT_BADGE_CLASS}`]: {
                    animation: `${keyframes`
                      0% { opacity: 0.3; }
                      50% { opacity: 1; }
                      100% { opacity: 0.3; }
                    `} 1.5s ease-in-out infinite`,
                  },
                  [`& .${EQUIPMENT_COUNT_BADGE_CLASS}:nth-child(1)`]: { animationDelay: '0s' },
                  [`& .${EQUIPMENT_COUNT_BADGE_CLASS}:nth-child(2)`]: { animationDelay: '0.3s' },
                  [`& .${EQUIPMENT_COUNT_BADGE_CLASS}:nth-child(3)`]: { animationDelay: '0.6s' },
                  [`& .${EQUIPMENT_COUNT_BADGE_CLASS}:nth-child(4)`]: { animationDelay: '0.9s' },
                }),
              }}
            >
              <EquipmentCountBadge count={errorNodesCount} status={TNodeStatus.ERROR} />
              <EquipmentCountBadge count={plannedNodesCount} status={TNodeStatus.PLANNED} />
              <EquipmentCountBadge count={offlineNodesCount} status={TNodeStatus.OFFLINE} />
              <EquipmentCountBadge count={onlineNodesCount} status={TNodeStatus.ACTIVE} />
            </div>
          )}
        </div>

        <Collapse expanded={opened}>
          <div
            style={{
              borderTop: '1px solid rgba(255, 255, 255, 0.3)',
              paddingTop: 10,
              paddingBottom: 10,
              marginTop: 10,
            }}
          >
            <div
              css={{
                display: 'grid',
                gridTemplateColumns: isWideScreen
                  ? PROPERTY_CARD_DIMENSIONS.wideScreen.gridTemplateColumns
                  : PROPERTY_CARD_DIMENSIONS.mobileScreen.gridTemplateColumns,
                gridColumnGap: 20,
                gridRowGap: 10,
              }}
            >
              <EquipmentStatusWithCountBadge count={errorNodesCount} status={TNodeStatus.ERROR} />
              <EquipmentStatusWithCountBadge count={plannedNodesCount} status={TNodeStatus.PLANNED} />
              <Tooltip
                label={`${getNodeStatusLabel(TNodeStatus.OFFLINE)} + ${getNodeStatusLabel(
                  TNodeStatus.PENDING,
                )}`}
              >
                <span>
                  <EquipmentStatusWithCountBadge count={offlineNodesCount} status={TNodeStatus.OFFLINE} />
                </span>
              </Tooltip>
              <EquipmentStatusWithCountBadge count={onlineNodesCount} status={TNodeStatus.ACTIVE} />
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255, 255, 255, 0.3)',
              paddingTop: 10,
              marginBottom: 10,
            }}
          >
            <Button
              onClick={() => handleClickViewProperty()}
              rightSection={
                <span style={{ transform: 'rotate(-90deg)' }}>
                  <IconChevronCircle />
                </span>
              }
              variant={'default'}
              style={{
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid white',
                width: '100%',
                marginTop: '10px',
              }}
            >
              {translate.phrases.placeholder('View Property')}
            </Button>
          </div>
        </Collapse>
      </div>
    </OverlayView>
  )
}
